/**
 * Review view for one hazard set (SPEC_Hazard_UI §3.2).
 *
 * Full screen, not a modal — the task is too long for an overlay.
 *
 * The two modes are deliberately different views, not one view with a flag:
 *  - delta shows three columns, the third ("unchanged") collapsed but present
 *    and submitted with the decision, because the expert attests the
 *    completeness of the whole set, not just of the change;
 *  - full shows a plain list with no change markers at all. Marking them would
 *    pull the eye to what is already known, which is the opposite of what a full
 *    review is for.
 */

import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Loader2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { HazardChip, damageTypeLabel } from "./hazardDisplay";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface Properties {
  regulatedObjectCode: string;
  onClose: (didChange: boolean) => void;
}

type Decision = "confirmed" | "rejected" | "partial";

export default function HazardReviewPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [preparation, setPreparation] = useState<any>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [note, setNote] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [qualification, setQualification] = useState("");
  const [perItem, setPerItem] = useState<Record<string, string>>({});
  const [partialMode, setPartialMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<Decision | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await legalAPI.prepareHazardReview(properties.regulatedObjectCode);
        if (cancelled) return;
        setPreparation(result);
        setQualification(result?.permissions?.qualification || "");
      } catch (error) {
        if (!cancelled) pushError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.regulatedObjectCode]);

  const submit = async (decision: Decision) => {
    if (!preparation) return;
    try {
      setSaving(true);
      await legalAPI.submitHazardReview({
        code: properties.regulatedObjectCode,
        mode: preparation.mode,
        decision,
        // Frozen with the review: what was shown is what gets recorded.
        delta: preparation.delta,
        unchangedMembers: preparation.unchangedMembers,
        fromSetVersion: preparation.fromSetVersion,
        trigger: preparation.trigger,
        perItemDecisions:
          decision === "partial"
            ? Object.entries(perItem).map(([hazardCode, itemDecision]) => ({
                hazardCode,
                decision: itemDecision,
              }))
            : undefined,
        note: note.trim() || undefined,
        signedBy: signedBy.trim() || undefined,
        qualification: qualification.trim() || undefined,
      });
      pushSuccess(t(`hazard.review.submitted.${decision}`));
      properties.onClose(true);
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  if (!preparation) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <Skeleton className="h-9 w-[220px]" />
        <Skeleton className="h-6 w-[320px] max-w-full" />
        <Skeleton className="h-[240px] w-full" />
      </div>
    );
  }

  const { mode, trigger, regulatedObject, delta, unchangedMembers, members, permissions } = preparation;
  const added = (delta || []).filter((entry: any) => entry.changeType === "added");
  const removed = (delta || []).filter((entry: any) => entry.changeType === "removed");
  const relationChanged = (delta || []).filter((entry: any) => entry.changeType === "relationChanged");
  const canConfirm = !!qualification.trim();
  const signedById = `${fieldId}-signed-by`;
  const qualificationId = `${fieldId}-qualification`;
  const noteId = `${fieldId}-note`;

  const perItemButtons = (hazardCode: string) => (
    <div className="mt-2 flex flex-wrap gap-2">
      <Button
        variant={perItem[hazardCode] === "confirmed" ? "default" : "outline"}
        size="sm"
        className="gap-1.5"
        aria-pressed={perItem[hazardCode] === "confirmed"}
        onClick={() => setPerItem({ ...perItem, [hazardCode]: "confirmed" })}
      >
        {perItem[hazardCode] === "confirmed" ? (
          <Check aria-hidden className="h-4 w-4" />
        ) : null}
        {t("hazard.review.itemConfirm")}
      </Button>
      <Button
        variant={perItem[hazardCode] === "rejected" ? "default" : "outline"}
        size="sm"
        className="gap-1.5"
        aria-pressed={perItem[hazardCode] === "rejected"}
        onClick={() => setPerItem({ ...perItem, [hazardCode]: "rejected" })}
      >
        {perItem[hazardCode] === "rejected" ? <Check aria-hidden className="h-4 w-4" /> : null}
        {t("hazard.review.itemReject")}
      </Button>
    </div>
  );

  const column = (title: string, entries: any[], render: (entry: any) => any) => (
    <div className="flex min-w-0 flex-col">
      <div className="border-b pb-1 font-semibold">
        {title} ({entries.length})
      </div>
      <div className="mt-2 flex min-w-0 flex-col gap-3">
        {entries.length === 0 ? (
          <span className="text-sm text-muted-foreground">—</span>
        ) : (
          entries.map(render)
        )}
      </div>
    </div>
  );

  const entryBlock = (entry: any) => (
    <div key={entry.hazardCode} className="min-w-0">
      <HazardChip
        label={`${entry.hazardCode} · ${entry.label || entry.hazardCode}`}
        damageType={entry.damageType}
        relation={entry.after || entry.before || "carries"}
        t={t}
      />
      <div className="mt-0.5 min-w-0 break-words text-xs text-muted-foreground">
        {entry.changeType === "relationChanged"
          ? t("hazard.review.relationChangedFromTo", {
              before: t(`hazard.relation.${entry.before}`),
              after: t(`hazard.relation.${entry.after}`),
            })
          : t(`hazard.relation.${entry.after || entry.before}`)}
        {entry.changeReason ? ` · ${entry.changeReason}` : ""}
      </div>
      {partialMode ? perItemButtons(entry.hazardCode) : null}
    </div>
  );

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-2 max-sm:w-full max-sm:justify-start"
          onClick={() => properties.onClose(false)}
        >
          <ArrowLeft aria-hidden className="h-4 w-4" />
          {t("hazard.review.backToQueue")}
        </Button>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="min-w-0 break-words text-lg font-semibold">{regulatedObject?.name}</h3>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t(`hazard.review.mode.${mode}`)} ·{" "}
          {t(`hazard.trigger.${trigger}`, { defaultValue: trigger })}
          {preparation.fromSetVersion !== null && preparation.fromSetVersion !== undefined
            ? ` · v${preparation.fromSetVersion} → v${preparation.toSetVersion}`
            : ` · v${preparation.toSetVersion}`}
        </p>
      </div>

      {mode === "delta" ? (
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6">
          {column(t("hazard.review.added"), [...added, ...relationChanged], entryBlock)}
          {column(t("hazard.review.removed"), removed, entryBlock)}
          <div className="flex min-w-0 flex-col">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 border-b pb-1 text-left font-semibold"
              aria-expanded={showUnchanged}
              onClick={() => setShowUnchanged(!showUnchanged)}
            >
              {showUnchanged ? (
                <ChevronDown aria-hidden className="h-4 w-4" />
              ) : (
                <ChevronRight aria-hidden className="h-4 w-4" />
              )}
              {t("hazard.review.unchanged")} ({unchangedMembers?.length || 0})
            </button>
            <div className="mt-2 flex min-w-0 flex-col gap-1">
              {showUnchanged ? (
                (unchangedMembers || []).map((member: any) => (
                  <div key={member.hazardCode} className="min-w-0">
                    <HazardChip
                      label={`${member.hazardCode} · ${member.label || member.hazardCode}`}
                      damageType={member.damageType}
                      relation={member.relation}
                      t={t}
                    />
                  </div>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t("hazard.review.unchangedCollapsedHint")}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 flex-col">
          <div className="border-b pb-1 font-semibold">
            {t("hazard.review.fullSet")} ({members?.length || 0})
          </div>
          <div className="mt-2 flex min-w-0 flex-col gap-2">
            {(members || []).map((member: any) => (
              <div key={member.hazardCode} className="min-w-0">
                <HazardChip
                  label={`${member.hazardCode} · ${member.label || member.hazardCode}`}
                  damageType={member.damageType}
                  relation={member.relation}
                  t={t}
                />
                <span className="text-xs text-muted-foreground">
                  {damageTypeLabel(t, member.damageType)}
                </span>
                {partialMode ? perItemButtons(member.hazardCode) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completion */}
      <div className="flex min-w-0 flex-col gap-4 border-t pt-4">
        <div className="flex min-w-0 flex-wrap gap-4">
          <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <Label htmlFor={signedById}>{t("hazard.review.signedByPlaceholder")}</Label>
            <Input
              id={signedById}
              value={signedBy}
              onChange={(event) => setSignedBy(event.target.value)}
            />
          </div>
          <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <Label htmlFor={qualificationId}>{t("hazard.review.qualificationPlaceholder")}</Label>
            <Input
              id={qualificationId}
              value={qualification}
              onChange={(event) => setQualification(event.target.value)}
            />
          </div>
          <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
            <Label htmlFor={noteId}>{t("hazard.review.notePlaceholder")}</Label>
            <Input id={noteId} value={note} onChange={(event) => setNote(event.target.value)} />
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {permissions?.canSignOff ? (
            <>
              <Button
                className="gap-2 max-sm:w-full"
                disabled={saving || !canConfirm}
                onClick={() => setPendingDecision("confirmed")}
              >
                {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                {t("hazard.review.confirmSet")}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                disabled={saving}
                onClick={() => setPendingDecision("rejected")}
              >
                {t("hazard.review.reject")}
              </Button>
              <Button
                variant="outline"
                className="max-sm:w-full"
                aria-pressed={partialMode}
                disabled={saving}
                onClick={() => setPartialMode(!partialMode)}
              >
                {t("hazard.review.partial")}
              </Button>
              {partialMode ? (
                <Button
                  className="max-sm:w-full"
                  disabled={saving || !canConfirm || Object.keys(perItem).length === 0}
                  onClick={() => setPendingDecision("partial")}
                >
                  {t("hazard.review.submitPartial")}
                </Button>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{t("hazard.review.noSignoffRight")}</p>
          )}
        </div>

        {permissions?.canSignOff && !canConfirm ? (
          <Alert variant="warning">
            <AlertDescription>{t("hazard.review.qualificationRequired")}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      {/* Die Entscheidung wird namentlich protokolliert und nur angehängt —
          deshalb ein Ausweg vor dem Schreiben. */}
      <AlertDialog
        open={!!pendingDecision}
        onOpenChange={(open) => {
          if (!open) setPendingDecision(null);
        }}
      >
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDecision ? t(`hazard.review.signDialogTitle.${pendingDecision}`) : ""}
            </AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {pendingDecision
                ? t(`hazard.review.signDialogMessage.${pendingDecision}`, {
                    type: regulatedObject?.name || properties.regulatedObjectCode,
                    qualification: qualification.trim(),
                    count: Object.keys(perItem).length,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                const decision = pendingDecision;
                setPendingDecision(null);
                if (decision) submit(decision);
              }}
            >
              {pendingDecision === "confirmed"
                ? t("hazard.review.confirmSet")
                : pendingDecision === "rejected"
                  ? t("hazard.review.reject")
                  : t("hazard.review.submitPartial")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
